import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Maximize2 } from 'lucide-react';
import { HOMES } from '@/content';
import { track } from '@/lib/track';
import FullscreenViewer from '@/components/FullscreenViewer';
import RedO from '@/components/RedO';

export default function Homes() {
  const [emblaRef, embla] = useEmblaCarousel({ loop: false, align: 'start' });
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(null);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setIndex(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on('select', onSelect);
    onSelect();
  }, [embla, onSelect]);

  // load the remaining chapter spreads as soon as the first content is ready
  useEffect(() => {
    const loadRest = () => HOMES.chapters.slice(1).forEach((c) => { const im = new Image(); im.src = c.image.full; });
    if (document.readyState === 'complete') loadRest();
    else window.addEventListener('load', loadRest, { once: true });
    return () => window.removeEventListener('load', loadRest);
  }, []);

  const go = (dir) => {
    if (!embla) return;
    if (dir > 0) { embla.scrollNext(); } else { embla.scrollPrev(); }
    track('config_select', { type: 'homes_chapter', dir });
  };

  return (
    <section id="homes" data-testid="homes-section" className="scroll-mt-20 overflow-hidden py-20 sm:py-28" aria-labelledby="homes-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow">{HOMES.eyebrow}</p>
            <h2 id="homes-heading" className="headline mt-3 text-3xl sm:text-5xl">
              <RedO text={HOMES.headline} />
            </h2>
          </div>
          <div className="hidden gap-2 sm:flex">
            <button data-testid="homes-prev-btn" aria-label="Previous chapter" onClick={() => go(-1)} className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E4E6EB] bg-white transition-colors hover:border-[#C92F4B] hover:text-[#C92F4B]">
              <ArrowLeft size={18} />
            </button>
            <button data-testid="homes-next-btn" aria-label="Next chapter" onClick={() => go(1)} className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E4E6EB] bg-white transition-colors hover:border-[#C92F4B] hover:text-[#C92F4B]">
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* data-lenis-prevent: horizontal swipes drive the carousel only — never the page scroll */}
      <div className="mt-10" ref={emblaRef} data-testid="homes-carousel" data-lenis-prevent>
        <div className="flex">
          {HOMES.chapters.map((c, i) => (
            <motion.article
              key={c.id}
              data-testid={`homes-chapter-${c.id}`}
              className="min-w-full px-4 sm:px-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              aria-label={`${c.title} — ${c.sub}`}
            >
              {/* one complete stitched spread per slide — container adapts to the image's own aspect ratio */}
              <div className="mx-auto max-w-6xl">
                <div
                  role="button"
                  tabIndex={0}
                  data-testid={`homes-image-${c.id}`}
                  aria-label={`${c.title} — view full screen`}
                  onClick={() => { track('image_fullscreen_intent', { chapter: c.id }); setZoom(c); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') setZoom(c); }}
                  className="group relative block w-full cursor-zoom-in overflow-hidden rounded-3xl border border-[#E4E6EB] bg-[#F4F3EE] shadow-[0_40px_80px_-40px_rgba(28,29,33,0.4)]"
                  style={{ aspectRatio: `${c.image.width} / ${c.image.height}` }}
                >
                  <picture>
                    <img
                      src={c.image.full}
                      srcSet={c.image.srcset}
                      sizes="(min-width: 1280px) 1152px, 100vw"
                      width={c.image.width}
                      height={c.image.height}
                      alt={c.alt}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      fetchPriority={i === 0 ? 'high' : 'auto'}
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-contain"
                    />
                  </picture>
                  <span className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#1C1D21]/85 text-white shadow-lg transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                    <Maximize2 size={17} />
                  </span>
                </div>

                {/* caption below the image — never covering faces or architecture */}
                <div className="mt-6 flex items-baseline justify-between gap-4">
                  <p className="font-display text-[11px] font-bold uppercase tracking-[0.3em] text-[#C92F4B]">{c.badge}</p>
                  <p className="font-display text-[11px] font-bold tracking-[0.3em] text-[#A7ABB0]">0{i + 1} / 03</p>
                </div>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-[#1C1D21] sm:text-4xl">
                  {c.title}
                </h3>
                <p className="font-display text-sm font-bold uppercase tracking-[0.22em] text-[#646A72]">{c.sub}</p>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#404145]">{c.text}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* progress dots — the travelling O */}
      <div className="mt-7 flex justify-center gap-3" role="tablist" aria-label="Home chapters">
        {HOMES.chapters.map((c, i) => (
          <button
            key={c.id}
            data-testid={`homes-dot-${c.id}`}
            role="tab"
            aria-selected={index === i}
            aria-label={`Go to chapter ${i + 1}`}
            onClick={() => embla && embla.scrollTo(i)}
            className={`h-3.5 w-3.5 rounded-full border-2 transition-[background-color,border-color,transform] duration-300 ${
              index === i ? 'scale-125 border-[#C92F4B] bg-[#C92F4B]' : 'border-[#A7ABB0] bg-transparent'
            }`}
          />
        ))}
      </div>

      {zoom && <FullscreenViewer src={zoom.image.full} alt={zoom.alt} onClose={() => setZoom(null)} />}
    </section>
  );
}
